import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import {
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
} from '@mui/material';
import {TreeItem as MUITreeItem} from '@mui/x-tree-view';
import {useFlags} from 'launchdarkly-react-client-sdk';
import PropTypes, {bool, func, object, objectOf} from 'prop-types';
import React, {useMemo} from 'react';
import {connect} from 'react-redux';
import {GOOD_ITEM_TYPES, ITEM_TYPES} from '@explorer/constants/item-types';
import {getAvailableItemTypes} from '@explorer/selectors/item-types';

const MED_RES_LABEL = 'Medium res';
const HIGH_RES_LABEL = 'High res';
const HYPERSPECTRAL_LABEL = 'Hyperspectral';

const ITEM_TYPE_RESOLUTION_LABEL = {
  [ITEM_TYPES.PSSCENE]: MED_RES_LABEL,
  [ITEM_TYPES.RESCENE]: MED_RES_LABEL,
  [ITEM_TYPES.REORTHOTILE]: MED_RES_LABEL,
  [ITEM_TYPES.SKYSATSCENE]: HIGH_RES_LABEL,
  [ITEM_TYPES.SKYSATCOLLECT]: HIGH_RES_LABEL,
  [ITEM_TYPES.PELICANSCENE]: HIGH_RES_LABEL,
  [ITEM_TYPES.TANAGERSCENE]: HYPERSPECTRAL_LABEL,
};

const ITEM_TYPES_LIST = Object.keys(GOOD_ITEM_TYPES);

const TreeItem = ({slotProps, ...props}) => (
  <MUITreeItem
    {...props}
    slotProps={{
      ...slotProps,
      content: {
        sx: {
          '&.Mui-selected, &.Mui-selected:hover, &.Mui-selected.Mui-focused': {
            backgroundColor: 'transparent',
          },
        },
      },
      checkbox: {
        ...slotProps?.checkbox,
        sx: {
          padding: 0.5,
        },
      },
    }}
  />
);

TreeItem.propTypes = {
  slotProps: PropTypes.object,
};

const SourcesPicker = ({selected, onChange, itemTypes}) => {
  const {explorerShowPelicanScene} = useFlags();

  const itemTypesList = useMemo(
    () =>
      explorerShowPelicanScene
        ? ITEM_TYPES_LIST
        : ITEM_TYPES_LIST.filter(
            itemType => itemType !== ITEM_TYPES.PELICANSCENE
          ),
    [explorerShowPelicanScene]
  );

  const handleItemSelectionToggle = event => {
    const {name, checked} = event.target;
    onChange({[name]: checked});
  };

  const someSelected = Object.values(selected).some(selection => !!selection);

  return (
    <FormControl
      component="fieldset"
      variant="standard"
      sx={{width: '100%'}}
      error={!someSelected}
    >
      <FormGroup sx={{gap: 0.5}}>
        {itemTypesList.map(itemType => (
          <FormControlLabel
            key={itemType}
            control={
              <>
                {itemType === ITEM_TYPES.TANAGERMETHANE && (
                  <SubdirectoryArrowRightIcon color="action" />
                )}
                <Checkbox
                  checked={!!selected[itemType]}
                  onChange={handleItemSelectionToggle}
                  name={itemType}
                />
              </>
            }
            label={
              <>
                {itemTypes?.typesMap?.[itemType]?.display_name || itemType}
                <Chip
                  size="small"
                  shape={null}
                  label={ITEM_TYPE_RESOLUTION_LABEL[itemType]}
                />
              </>
            }
            slotProps={{
              typography: {
                sx: {
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                },
              },
            }}
            sx={{
              marginLeft: itemType === ITEM_TYPES.TANAGERMETHANE ? 2 : 0,
              marginRight: 0,
            }}
          />
        ))}
      </FormGroup>
      <FormHelperText color="error">
        Select at least one (1) imagery type
      </FormHelperText>
    </FormControl>
  );
};

SourcesPicker.propTypes = {
  selected: objectOf(bool).isRequired,
  itemTypes: object.isRequired,
  onChange: func.isRequired,
};

const mapStateToProps = state => ({
  itemTypes: getAvailableItemTypes(state),
});

export default connect(mapStateToProps)(SourcesPicker);
